import { LoyaltyPointsBanner } from 'components/OnlineBooking/Resume/LoyaltyPointsBanner/LoyaltyPointsBanner';
import { PromoCodeField } from 'components/OnlineBooking/Resume/PromoCode/PromoCodeField';
import { PetAvatar } from 'components/Pet/Avatar/PetAvatar';
import { PetTypeChip } from 'components/Pet/TypeChip/PetTypeChip';
import { useLoyaltyEstimate } from 'hooks/useLoyaltyEstimate';
import React from 'react';
import { useSelector } from 'react-redux';
import { getLoyaltyPointsToEarn, getShowLoyaltyBanner } from 'reducers/loyalty/LoyaltySelectors';
import { getMarketplace } from 'reducers/marketplace/MarketplaceSelectors';
import {
    getOnlineBookingCurrentStep,
    getOnlineBookingDepositRequired,
    getOnlineBookingItemsGroupedByPet,
    getOnlineBookingWaitlistItemsGroupedByPet,
    getOnlineBookingWaitlistTotalAmount
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { getSurchargeQuote } from 'reducers/paymentMethods/PaymentMethodsSelectors';
import { getResumePricing } from 'reducers/promocodes/PromocodesSelectors';
import { RootState } from 'store';
import { AddonService } from 'types/AddonService';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { DAYCARE_DURATION_OPTIONS } from 'types/Service';
import {
    isOnlineBookingBoardingItem,
    isOnlineBookingDaycareItem,
    isOnlineBookingEvaluationItem,
    isOnlineBookingGroomingItem,
    isOnlineBookingSessionItemOf,
    OnlineBookingEvaluationItem,
    OnlineBookingGroomingItem,
    OnlineBookingItem,
    OnlineBookingPricingOptions,
    OnlineBookingStep,
    SESSION_BUSINESS_AREAS
} from 'types/OnlineBooking';
import { deriveWalkingResumeRow, SESSION_AREA_COPY } from 'types/Walking';
import { calculateDaycareHours, getServicePriceByPet } from 'utils/serviceUtils';
import { describeScheduleOn } from 'utils/addonsMode';
import { getAddonScheduleDates, getOnlineBookingAddonUnits } from 'utils/onlineBookingAddons';
import classes from './OnlineBookingResume.module.scss';

// An Add-On's total, with "Everyday" / "N selected day(s)" under its name (Figma 1930:6228). One
// with no days (grooming, walking, training) has only a quantity: "name x N" when it is not just one.
const ResumeAddonLine: React.FC<{ item: OnlineBookingItem; addon: AddonService }> = ({
    item,
    addon
}) => {
    const units = getOnlineBookingAddonUnits(item, addon);
    const dates = getAddonScheduleDates(item);
    const schedule = item.addonSchedules?.[addon.id];

    return (
        <div className={classes.itemLine}>
            <span>
                {dates.length === 0 && units > 1 ? `${addon.name} x ${units}` : addon.name}
                {schedule && dates.length > 0 && (
                    <>
                        <br />
                        <mark>{describeScheduleOn(schedule, dates)}</mark>
                    </>
                )}
            </span>
            <span>${(addon.price * units).toFixed(2)}</span>
        </div>
    );
};

interface OnlineBookingResumeProps {
    /**
     * Waitlist mode: only the items of the waitlist business area, without the
     * per-service date & time, and no deposit / loyalty information.
     */
    waitlist?: boolean;
    // Waitlist mode: price one occurrence of each service instead of the dates picked in the cart
    waitlistAsap?: boolean;
}

export const OnlineBookingResume: React.FC<OnlineBookingResumeProps> = ({
    waitlist = false,
    waitlistAsap = false
}) => {
    const marketplace = useSelector(getMarketplace);
    const allBookingItems = useSelector(getOnlineBookingItemsGroupedByPet);
    const waitlistBookingItems = useSelector(getOnlineBookingWaitlistItemsGroupedByPet);
    const resumePricing = useSelector(getResumePricing);
    const waitlistSubtotal = useSelector((state: RootState) =>
        getOnlineBookingWaitlistTotalAmount(state, waitlistAsap)
    );
    const bookingDepositEnabled = useSelector(getOnlineBookingDepositRequired);
    const currentStep = useSelector(getOnlineBookingCurrentStep);
    const surchargeQuote = useSelector(getSurchargeQuote);
    const loyaltyBannerEnabled = useSelector(getShowLoyaltyBanner);
    const loyaltyPointsToEarn = useSelector(getLoyaltyPointsToEarn);

    useLoyaltyEstimate();

    const bookingItems = waitlist ? waitlistBookingItems : allBookingItems;
    const subtotal = waitlist ? waitlistSubtotal : resumePricing.subtotal;
    // A waitlist request is not charged: no promo code, deposit or amount due
    const discount = waitlist ? null : resumePricing.discount;
    const { deposit, amountDue } = resumePricing;
    const depositEnabled = !waitlist && bookingDepositEnabled;
    const showLoyaltyBanner = !waitlist && loyaltyBannerEnabled;
    const showSchedule = !waitlist;
    // The waitlist request prices the stay for the picked dates unless it goes ASAP
    const showStayPrice = showSchedule || !waitlistAsap;

    const hasSurcharge = !!surchargeQuote && surchargeQuote.surcharge > 0;
    const showPromoCode =
        !waitlist &&
        (currentStep === OnlineBookingStep.Agreements || currentStep === OnlineBookingStep.Checkout);
    const showAmountDue = depositEnabled || discount != null;

    const bookingHasServices = React.useMemo(() => {
        return bookingItems.length > 0 && bookingItems.some(item => item.items.length > 0);
    }, [bookingItems]);

    return (
        <div className={classes.root}>
            {showLoyaltyBanner && <LoyaltyPointsBanner pointsToEarn={loyaltyPointsToEarn} />}

            <div className={classes.header}>
                <h1 className={classes.title}>Booking details</h1>
            </div>

            {bookingItems.length > 0 && (
                <div className={classes.bookingItems}>
                    {bookingItems.map(item => {
                        const groomingItems = item.items.filter(
                            item =>
                                isOnlineBookingGroomingItem(item) ||
                                (isOnlineBookingEvaluationItem(item) &&
                                    [
                                        MarketplaceServiceCategory.GROOMING,
                                        MarketplaceServiceCategory.DENTAL
                                    ].includes(item.evaluationBusinessArea))
                        );
                        const groomingAddons = groomingItems.flatMap(item =>
                            item.addons.map(addon => ({ item, addon }))
                        );
                        const firstGroomingItem = groomingItems[0] as
                            | OnlineBookingGroomingItem
                            | OnlineBookingEvaluationItem;

                        const daycareItems = item.items.filter(isOnlineBookingDaycareItem);
                        const daycareAddons = daycareItems.flatMap(item =>
                            item.addons.map(addon => ({ item, addon }))
                        );
                        const firstDaycareItem = daycareItems[0];
                        const daycareDaysCount = firstDaycareItem?.days?.length ?? 0;

                        const boardingItems = item.items.filter(isOnlineBookingBoardingItem);
                        const boardingAddons = boardingItems.flatMap(item =>
                            item.addons.map(addon => ({ item, addon }))
                        );
                        const firstBoardingItem = boardingItems[0];

                        const sessionSections = SESSION_BUSINESS_AREAS.map(businessArea => {
                            const walkingRows = item.items
                                .filter(isOnlineBookingSessionItemOf(businessArea))
                                .flatMap(walkingItem => {
                                    const row = deriveWalkingResumeRow(walkingItem);

                                    return row ? [{ walkingItem, row }] : [];
                                });

                            return {
                                businessArea,
                                walkingRows,
                                firstWalkingRow: walkingRows[0]?.row
                            };
                        });

                        return (
                            <div key={item.pet.uuid} className={classes.bookingItem}>
                                <div className={classes.bookingItemPet}>
                                    <PetAvatar
                                        size={56}
                                        image={item.pet.image?.src}
                                        petType={item.pet.type}
                                    />
                                    <div className={classes.bookingItemInfo}>
                                        <p className={classes.petName}>
                                            {item.pet.name}
                                            <PetTypeChip petType={item.pet.type} />
                                        </p>
                                        <p className={classes.petInfo}>
                                            {item.pet.breed?.name ?? 'Not Defined'} -{' '}
                                            {item.pet.size.name}
                                            {/* TODO: remove temp data */}
                                            {!!item.pet.weight && ` - ${item.pet.weight} lbs`}
                                        </p>
                                    </div>
                                </div>

                                {groomingItems.length > 0 &&
                                    groomingItems.some(item => item.service) && (
                                        <div className="flex flex-col gap-3">
                                            <div className={classes.subtitle}>
                                                <span>Grooming Services</span>

                                                {showSchedule && (
                                                    <span>
                                                        {firstGroomingItem.date?.format(
                                                            'MMM DD, YYYY'
                                                        )}

                                                        {firstGroomingItem.time &&
                                                            ` - ${firstGroomingItem.time.label}`}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex flex-col gap-2">
                                                {groomingItems.map(item => {
                                                    if (!item.service) return null;
                                                    return (
                                                        <div
                                                            key={item.uuid}
                                                            className={classes.itemLine}
                                                        >
                                                            <span>{item.service?.name}</span>
                                                            <span>
                                                                $
                                                                {getServicePriceByPet(
                                                                    item.service,
                                                                    item.pet
                                                                ).toFixed(2)}
                                                            </span>
                                                        </div>
                                                    );
                                                })}

                                                {groomingAddons.map(
                                                    ({ item: addonItem, addon }) => (
                                                        <ResumeAddonLine
                                                            key={`${item.pet.id}-${addon.id}`}
                                                            item={addonItem}
                                                            addon={addon}
                                                        />
                                                    )
                                                )}

                                                {!isOnlineBookingEvaluationItem(
                                                    firstGroomingItem
                                                ) && (
                                                    <div className={classes.itemLine}>
                                                        <span>
                                                            <i>Staff</i>{' '}
                                                            <b>
                                                                {firstGroomingItem.preferredStaff
                                                                    ?.name ?? 'No preference'}
                                                            </b>
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                {daycareItems.length > 0 &&
                                    daycareItems.some(item => item.service) && (
                                        <div className="flex flex-col gap-3">
                                            <div className={classes.subtitle}>
                                                <span>Daycare Services</span>

                                                {showSchedule && (
                                                    <span>
                                                        {daycareDaysCount > 0 &&
                                                            `${daycareDaysCount} ${
                                                                daycareDaysCount === 1
                                                                    ? 'day'
                                                                    : 'days'
                                                            }`}

                                                        {firstDaycareItem.dropOffTime &&
                                                            ` · ${firstDaycareItem.dropOffTime.label}`}

                                                        {firstDaycareItem.pickUpTime &&
                                                            ` - ${firstDaycareItem.pickUpTime.label}`}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex flex-col gap-2">
                                                {daycareItems.map(item => {
                                                    if (!item.service) return null;

                                                    const hourlyPrice = getServicePriceByPet(
                                                        item.service,
                                                        item.pet
                                                    );

                                                    if (
                                                        showStayPrice &&
                                                        item.service?.daycareMetadata
                                                            ?.durationOption ===
                                                            DAYCARE_DURATION_OPTIONS.HOURLY &&
                                                        item.dropOffTime &&
                                                        item.pickUpTime
                                                    ) {
                                                        const hours = calculateDaycareHours(
                                                            item.dropOffTime,
                                                            item.pickUpTime
                                                        );
                                                        const totalPrice = hourlyPrice * hours;

                                                        return (
                                                            <div
                                                                key={item.uuid}
                                                                className={classes.itemLine}
                                                            >
                                                                <span>{item.service?.name}</span>
                                                                <span>
                                                                    ${hourlyPrice.toFixed(2)}/Hour
                                                                    <br />
                                                                    <mark>
                                                                        ${totalPrice.toFixed(2)} per
                                                                        stay
                                                                    </mark>
                                                                </span>
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <div
                                                            key={item.uuid}
                                                            className={classes.itemLine}
                                                        >
                                                            <span>{item.service?.name}</span>
                                                            <span>${hourlyPrice.toFixed(2)}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {daycareAddons.map(({ item: addonItem, addon }) => (
                                                <ResumeAddonLine
                                                    key={`${item.pet.id}-${addon.id}`}
                                                    item={addonItem}
                                                    addon={addon}
                                                />
                                            ))}
                                        </div>
                                    )}

                                {boardingItems.length > 0 &&
                                    boardingItems.some(item => item.service) && (
                                        <div className="flex flex-col gap-3">
                                            <div className={classes.subtitle}>
                                                <span>Boarding Services</span>

                                                {showSchedule && (
                                                    <span>
                                                        {firstBoardingItem.endDate &&
                                                            `${firstBoardingItem.startDate?.format(
                                                                'MMM DD, YYYY'
                                                            )} - ${firstBoardingItem.endDate.format(
                                                                'MMM DD, YYYY'
                                                            )}`}

                                                        {firstBoardingItem.checkInTime &&
                                                            firstBoardingItem.checkOutTime &&
                                                            ` · ${firstBoardingItem.checkInTime.label} - ${firstBoardingItem.checkOutTime.label}`}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex flex-col gap-2">
                                                {boardingItems.map(item => {
                                                    if (!item.service) return null;

                                                    const hasDates =
                                                        showStayPrice &&
                                                        !!item.startDate &&
                                                        !!item.endDate;

                                                    return (
                                                        <div
                                                            key={item.uuid}
                                                            className={classes.itemLine}
                                                        >
                                                            <span>{item.service?.name}</span>
                                                            <span>
                                                                $
                                                                {getServicePriceByPet(
                                                                    item.service,
                                                                    item.pet
                                                                ).toFixed(2)}
                                                                /Night
                                                                {hasDates && (
                                                                    <>
                                                                        <br />
                                                                        <mark>
                                                                            $
                                                                            {getServicePriceByPet(
                                                                                item.service,
                                                                                item.pet,
                                                                                item.endDate?.diff(
                                                                                    item.startDate,
                                                                                    'days'
                                                                                )
                                                                            ).toFixed(2)}{' '}
                                                                            per stay
                                                                        </mark>
                                                                    </>
                                                                )}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {boardingAddons.map(({ item: addonItem, addon }) => (
                                                <ResumeAddonLine
                                                    key={`${item.pet.id}-${addon.id}`}
                                                    item={addonItem}
                                                    addon={addon}
                                                />
                                            ))}
                                        </div>
                                    )}

                                {sessionSections.map(
                                    ({ businessArea, walkingRows, firstWalkingRow }) =>
                                        walkingRows.length > 0 &&
                                        firstWalkingRow && (
                                            <div key={businessArea} className="flex flex-col gap-3">
                                                <div className={classes.subtitle}>
                                                    <span>{`${SESSION_AREA_COPY[businessArea].label} Services`}</span>

                                                    <span>{firstWalkingRow.dateTimeLabel}</span>
                                                </div>

                                                <div className="flex flex-col gap-2">
                                                    {walkingRows.map(({ walkingItem, row }) => {
                                                        if (!walkingItem.service) return null;
                                                        return (
                                                            <div
                                                                key={walkingItem.uuid}
                                                                className={classes.itemLine}
                                                            >
                                                                <span>{row.serviceName}</span>
                                                                <span>
                                                                    $
                                                                    {getServicePriceByPet(
                                                                        walkingItem.service,
                                                                        walkingItem.pet
                                                                    ).toFixed(2)}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}

                                                    <div className={classes.itemLine}>
                                                        <span>
                                                            <i>Staff</i>{' '}
                                                            <b>{firstWalkingRow.staffLabel}</b>
                                                        </span>
                                                    </div>
                                                </div>

                                                {walkingRows.flatMap(({ walkingItem }) =>
                                                    walkingItem.addons.map(addon => (
                                                        <ResumeAddonLine
                                                            key={`${item.pet.id}-${addon.id}`}
                                                            item={walkingItem}
                                                            addon={addon}
                                                        />
                                                    ))
                                                )}
                                            </div>
                                        )
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {bookingHasServices && showPromoCode && <PromoCodeField />}

            {bookingHasServices && (
                <>
                    <div className={classes.total}>
                        <p>
                            {marketplace.onlineBookingPreferences.pricingOption ===
                            OnlineBookingPricingOptions.StartingAt
                                ? 'Starting at'
                                : 'Price'}
                        </p>

                        <p>${subtotal.toFixed(2)}</p>
                    </div>

                    {discount != null && (
                        <div className={classes.discount}>
                            <p>Discount</p>
                            <p>-${discount.toFixed(2)}</p>
                        </div>
                    )}

                    {showAmountDue && (
                        <div className={classes.deposit}>
                            {depositEnabled && (
                                <div>
                                    <p>Deposit to book</p>
                                    <p>${deposit.toFixed(2)}</p>
                                </div>
                            )}

                            {depositEnabled && hasSurcharge && (
                                <>
                                    <div>
                                        <p>Surcharge</p>
                                        <p>${surchargeQuote!.surcharge.toFixed(2)}</p>
                                    </div>

                                    <div>
                                        <p>Total due now</p>
                                        <p>${surchargeQuote!.total.toFixed(2)}</p>
                                    </div>
                                </>
                            )}

                            <div>
                                <p>
                                    {depositEnabled
                                        ? 'Estimated total due at appointment'
                                        : 'Estimated total due'}
                                </p>
                                <p>${amountDue.toFixed(2)}</p>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
